import React, { useState } from 'react';
import {
    Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody,
    ModalFooter, ModalCloseButton, FormControl, FormLabel,
    Input, Button, useColorMode, useToast,
} from '@chakra-ui/react';
import axios from '../utils/axiosConfig';

const NewAssistantModal = ({ isOpen, onClose, onCreated }) => {
    const [name, setName] = useState('');
    const { colorMode } = useColorMode();
    const toast = useToast();

    const handleCreate = async () => {
        try {
            const newAssistant = {
                name,
                firstMessage: 'Hello! I am your AI assistant. How can I help you today?',
                systemPrompt: 'You are a helpful AI assistant. Your goal is to assist users with their questions and tasks in a clear, concise, and professional manner. Always be polite and respectful.',
                temperature: 0.7,
                maxTokens: 4096,
                idleMessage: 'Is there anything else I can help you with?',
                transcriptionModelId: 'nova-3',
                inputAudioNoiseReduction: 'none',
                cartesiaVoiceId: 'openai-alloy',
                voiceProvider: 'OpenAI',
                callTimeout: 30,
                hangupTimeout: 180,
                twilioRecording: false,
                attachConversationHistory: false,
                ignoreAudioDuringGreeting: false,
                allowInterruptionDuringGreeting: true,
                model: 'realtime-audio:gpt-4o-mini-realtime-preview-2024-12-17',
                knowledgebases: [],
                tools: [],
                mcps: [],
                voicemailAction: 'skip',
                vadThreshold: 0.5,
                vadSilenceDurationMs: 500,
                vadPrefixPaddingMs: 300,
                vadType: 'server_vad',
                vadEagerness: 'medium',
                realtimeSpeed: 1.0,
            };

            const response = await axios.post('/api/protected/assistants', newAssistant);
            if (response.data.success) {
                onCreated(response.data.data);
                toast({
                    title: 'Assistant created',
                    description: 'New assistant has been created successfully.',
                    status: 'success',
                    duration: 3000,
                    isClosable: true,
                });
                setName('');
                onClose();
            }
        } catch (error) {
            console.error('Error creating assistant:', error);
            toast({
                title: 'Error',
                description: 'Failed to create new assistant. Please try again.',
                status: 'error',
                duration: 3000,
                isClosable: true,
            });
        }
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose}>
            <ModalOverlay />
            <ModalContent bg={colorMode === 'light' ? 'white' : 'gray.800'}>
                <ModalHeader color={colorMode === 'light' ? 'gray.800' : 'white'}>Create New Assistant</ModalHeader>
                <ModalCloseButton color={colorMode === 'light' ? 'gray.600' : 'gray.400'} />
                <ModalBody>
                    <FormControl>
                        <FormLabel color={colorMode === 'light' ? 'gray.700' : 'white'}>Assistant Name</FormLabel>
                        <Input
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            placeholder="Enter assistant name"
                            autoFocus
                            bg={colorMode === 'light' ? 'white' : 'gray.700'}
                            color={colorMode === 'light' ? 'gray.800' : 'white'}
                            borderColor={colorMode === 'light' ? 'gray.200' : 'gray.600'}
                            _hover={{
                                borderColor: colorMode === 'light' ? 'gray.300' : 'gray.500'
                            }}
                            _focus={{
                                borderColor: 'blue.500',
                                boxShadow: '0 0 0 1px var(--chakra-colors-blue-500)'
                            }}
                            _placeholder={{
                                color: colorMode === 'light' ? 'gray.400' : 'gray.500'
                            }}
                        />
                    </FormControl>
                </ModalBody>
                <ModalFooter>
                    <Button
                        variant="ghost"
                        mr={3}
                        onClick={onClose}
                        color={colorMode === 'light' ? 'gray.600' : 'gray.300'}
                        _hover={{
                            bg: colorMode === 'light' ? 'gray.100' : 'gray.700'
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        colorScheme="brand"
                        onClick={handleCreate}
                        isDisabled={!name.trim()}
                    >
                        Create
                    </Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
};

export default NewAssistantModal;
